import React, {
  forwardRef,
  useRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useCallback,
} from "react";
import { Container } from "@pixi/react";
import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import PotCollectionOverlaySymbolController from "./PotCollectionOverlaySymbolController";
import { Settings } from "../../settings";
import {
  isBonusSymbol,
  getSymbolZIndex,
  COIN_SYMBOL_IDS,
  ZEUS_MYSTERY_ID,
  ZEUS_DOUBLE_ID,
  ZEUS_COLLECT_ID,
  CD_MULT_ID,
  BLANK_SYMBOL_ID,
  JACKPOT_VALUE_TO_ID,
} from "../../SymbolData";
import store from "../../State/Store/store";

/**
 * PotCollectionOverlaySymbol
 *
 * Container for spine animations in Pot Collection feature.
 * Each symbol position has its own controller that manages coin/jackpot/zeus spines.
 * Mirrors RoyalCollectOverlaySymbol pattern.
 *
 * Tension layer: a SEPARATE container (tensionContainerRef) rendered on top of the
 * main overlay container. It manages a symbol_anticipation Spine instance independently
 * from the coin/win spine logic. Same position, different layer.
 */
const PotCollectionOverlaySymbol = forwardRef(
  ({ index, reelIndex, x = 0, y, symbolWidth, symbolHeight }, ref) => {
    const containerRef = useRef(null);
    const outerContainerRef = useRef(null);
    const controllerRef = useRef(null);
    // ── Tension layer (separate from coin/win overlay) ──
    const tensionContainerRef = useRef(null);
    const tensionSpineRef = useRef(null);
    const tensionVisibleRef = useRef(false);

    const containerName = useMemo(
      () => `pc_overlay_symbol_${reelIndex}_${index}`,
      [reelIndex, index],
    );

    // Initialize controller once.
    // If the Pixi Container ref isn't populated yet on the first effect run
    // (possible on slow frames), retry on the next animation frame.
    useEffect(() => {
      let rafId = null;

      const tryInit = () => {
        if (controllerRef.current) return; // Already initialized

        if (!containerRef.current) {
          // Container not mounted yet — defer one frame and retry
          rafId = requestAnimationFrame(tryInit);
          return;
        }

        controllerRef.current = new PotCollectionOverlaySymbolController(
          containerRef.current,
          reelIndex,
          index,
          symbolWidth,
          symbolHeight,
        );

        // Show idle animations for symbols already on the grid at mount time.
        //
        // Normal entry: only Zeus orbs (cd/cm/cc — isBonusSymbol) are on the grid.
        //   Regular coins (15) and jackpots (17-19) land during respins via feature executor.
        //
        // Mid-bonus reload (reloadPhase === "respins"): ALL non-blank symbols are already
        //   on the grid — coins, jackpots, and zeus orbs. Show idle for all of them.
        //   Zeus orbs show post-action idle (e.g. CD_Green/idle_cd) with final values.
        //   All feature actions are already applied — show the result state.
        const state = store.getState();
        const initialReels = state.potCollection?.initialRespinScreen;
        const isMidBonusReload = state.potCollection?.reloadPhase === "respins"
          || state.potCollection?.reloadPhase === "collection";
        if (initialReels) {
          const symbolId = parseInt(initialReels?.[reelIndex]?.[index], 10);
          const shouldShowIdle = isMidBonusReload
            ? (symbolId && symbolId !== BLANK_SYMBOL_ID) // All non-blank
            : isBonusSymbol(symbolId);       // Zeus orbs only
          if (shouldShowIdle) {
            if (isMidBonusReload && isBonusSymbol(symbolId)) {
              // Zeus orbs on reload: show post-action idle (features already applied).
              // CD_Green/idle_cd, CC_Blue/idle_cc — these are the final coin states
              // after doubling/collecting. CM transforms to jackpot (handled below).
              const POST_ACTION_IDLE = {
                [ZEUS_DOUBLE_ID]: "CD_Green/idle_cd",   // cd  → double done
                [CD_MULT_ID]: "CD_Green/idle_cd",       // cd_mult
                [ZEUS_COLLECT_ID]: "CC_Blue/idle_cc",   // cc  → collect done
                25: "CC_Blue/idle_cc",                  // cc_empty
              };
              const postActionAnim = POST_ACTION_IDLE[symbolId];
              if (postActionAnim) {
                // Play post-action idle on cs spine with value text
                controllerRef.current.playNamedAnimation(postActionAnim, true, null, symbolId);
                // Show post-feature value (skip doubling division)
                controllerRef.current.showReloadCoinValue(symbolId);
              } else if (symbolId === ZEUS_MYSTERY_ID) {
                // CM (mystery) — may or may not have been FLIPPED by Double or Collect.
                // m_reveals only means "activated → jackpot" — NOT flipped to regular coin.
                // Only d_doubles or c_collect flip a jackpot → regular coin with value.
                const flippedPositions = state.potCollection?.flippedCoinPositions || [];
                const posKey = `${reelIndex}_${index}`;
                const wasFlipped = flippedPositions.includes(posKey);
                const COIN_ID = COIN_SYMBOL_IDS[0];

                if (wasFlipped) {
                  // Mystery was flipped by Double or Collect → regular coin with current value.
                  controllerRef.current.showIdleAnimation(COIN_ID);
                  controllerRef.current.showReloadCoinValue(COIN_ID);
                } else {
                  // Mystery was NOT flipped — still a jackpot coin (j.json).
                  // Show tier skin (MINI/MINOR/MAJOR), no value text.
                  // Values only show at end-of-bonus collection.
                  const currentValues = state.potCollection?.valuesGrid
                    || state.potCollection?.initialRespinValues;
                  const bet = state.game?.bet || 1;
                  const coinValue = currentValues?.[reelIndex]?.[index] || 0;
                  const multiplier = coinValue > 0 ? Math.round(coinValue / bet) : 0;
                  const jpId = JACKPOT_VALUE_TO_ID[multiplier];
                  controllerRef.current.showIdleAnimation(jpId || COIN_ID);
                  // Intentionally NO showReloadCoinValue — jackpots reveal at end-of-bonus only.
                }
              }
            } else {
              controllerRef.current.showIdleAnimation(symbolId);
              // On reload, override coin value with post-feature value (no doubling division)
              if (isMidBonusReload && symbolId === COIN_SYMBOL_IDS[0]) {
                controllerRef.current.showReloadCoinValue(symbolId);
              }
            }

            // Z-index priority — see getSymbolZIndex for full hierarchy
            if (outerContainerRef.current) {
              outerContainerRef.current.zIndex = getSymbolZIndex(symbolId);
            }
          }
        }
      };

      tryInit();

      return () => {
        if (rafId !== null) cancelAnimationFrame(rafId);
        if (controllerRef.current) {
          controllerRef.current.cleanup();
          controllerRef.current = null;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Helper: apply tension spine scale (no manual offsets — rely on spine's authored position)
    const applyTensionScaling = useCallback((spine, w, h) => {
      if (!spine || spine.destroyed) return;
      const nativeW = Settings.SYMBOL_WIDTH;
      const nativeH = Settings.SYMBOL_HEIGHT;
      spine.scale.set(w / nativeW, h / nativeH);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Initialize tension spine once (pre-created, hidden until needed)
    useEffect(() => {
      if (!tensionContainerRef.current) return;

      const skeletonAlias = "symbol_anticipation-skeleton";
      const atlasAlias = "symbol_anticipation-atlas";
      if (!PIXI.Assets.cache.has(skeletonAlias) || !PIXI.Assets.cache.has(atlasAlias)) {
        return;
      }

      try {
        const spine = Spine.from({ skeleton: skeletonAlias, atlas: atlasAlias });
        tensionSpineRef.current = spine;

        applyTensionScaling(spine, symbolWidth, symbolHeight);

        // Start hidden
        spine.alpha = 0.001;
        spine.autoUpdate = false;
        tensionContainerRef.current.addChild(spine);
      } catch (err) {

      }

      return () => {
        if (tensionSpineRef.current) {
          if (tensionSpineRef.current.parent) {
            tensionSpineRef.current.parent.removeChild(tensionSpineRef.current);
          }
          if (!tensionSpineRef.current.destroyed) {
            tensionSpineRef.current.destroy({ children: true, texture: false, baseTexture: false });
          }
          tensionSpineRef.current = null;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle resize for both overlay controller and tension spine
    useEffect(() => {
      if (controllerRef.current) {
        controllerRef.current.resizeAnimations(symbolWidth, symbolHeight);
      }
      applyTensionScaling(tensionSpineRef.current, symbolWidth, symbolHeight);
    }, [symbolWidth, symbolHeight, applyTensionScaling]);

    // Memoize exposed methods
    const getController = useCallback(() => controllerRef.current, []);

    const showLandingAnimation = useCallback((symbolId) => {
      controllerRef.current?.showLandingAnimation(symbolId);
    }, []);

    const showIdleAnimation = useCallback((symbolId) => {
      controllerRef.current?.showIdleAnimation(symbolId);
    }, []);

    const showWinAnimation = useCallback(
      (symbolId, loop = true, showText = true) => {
        controllerRef.current?.showWinAnimation(symbolId, loop, showText);
      },
      [],
    );

    const playNamedAnimation = useCallback(
      (animationName, loop = false, onComplete = null, symbolId = null) => {
        controllerRef.current?.playNamedAnimation(animationName, loop, onComplete, symbolId);
      },
      [],
    );

    const queueNamedAnimation = useCallback(
      (animationName, loop = false) => {
        controllerRef.current?.queueNamedAnimation(animationName, loop);
      },
      [],
    );

    const hideAnimation = useCallback(() => {
      controllerRef.current?.hideAnimation();
    }, []);

    const getActiveSpine = useCallback(() => {
      return controllerRef.current?.getActiveSpine() || null;
    }, []);

    const getContainer = useCallback(() => containerRef.current, []);

    // ── Tension layer API (completely separate from coin/win spine) ──

    const playTensionIn = useCallback(() => {
      const spine = tensionSpineRef.current;
      if (!spine || spine.destroyed || !spine.state) return;

      tensionVisibleRef.current = true;

      // Reset state and set animation BEFORE making visible — prevents setup pose flash
      spine.state.clearTracks();
      spine.state.clearListeners();
      spine.skeleton.setToSetupPose();

      // Set animation first, then apply so the first visible frame is the "in" animation
      spine.state.setAnimation(0, "in", false);
      spine.state.addAnimation(0, "idle", true, 0);
      spine.state.update(0);
      spine.state.apply(spine.skeleton);

      // NOW make visible — first rendered frame is "in" frame 0, not setup pose
      spine.alpha = 1;
      spine.autoUpdate = true;
      spine.state.timeScale = 1;
    }, []);

    const playTensionOut = useCallback(() => {
      const spine = tensionSpineRef.current;
      if (!spine || spine.destroyed || !spine.state) return;

      // If already playing out, don't restart
      const currentAnim = spine.state.getCurrent(0);
      if (currentAnim && currentAnim.animation.name === "out") return;

      spine.state.clearListeners();
      spine.state.setAnimation(0, "out", false);

      spine.state.addListener({
        complete: (entry) => {
          if (entry.animation.name === "out") {
            spine.alpha = 0.001;
            spine.autoUpdate = false;
            tensionVisibleRef.current = false;
          }
        },
      });
    }, []);

    const hideTension = useCallback(() => {
      const spine = tensionSpineRef.current;
      if (!spine || spine.destroyed) {
        tensionVisibleRef.current = false;
        return;
      }

      spine.alpha = 0.001;
      spine.autoUpdate = false;
      tensionVisibleRef.current = false;

      if (spine.state) {
        try {
          spine.state.clearTracks();
          spine.state.clearListeners();
        } catch (_) {
          // Fail silently
        }
      }
      if (spine.skeleton?.setToSetupPose) {
        spine.skeleton.setToSetupPose();
      }
    }, []);

    const isTensionVisible = useCallback(() => {
      return tensionVisibleRef.current;
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        getController,
        showLandingAnimation,
        showIdleAnimation,
        showWinAnimation,
        playNamedAnimation,
        queueNamedAnimation,
        hideAnimation,
        getActiveSpine,
        getContainer,
        // ── Z-index control (flat overlay) ──
        setZIndex: (z) => {
          if (outerContainerRef.current) outerContainerRef.current.zIndex = z;
        },
        resetZIndex: () => {
          if (outerContainerRef.current) outerContainerRef.current.zIndex = 1;
        },
        // ── Tension API (separate layer) ──
        playTensionIn,
        playTensionOut,
        hideTension,
        isTensionVisible,
      }),
      [
        getController,
        showLandingAnimation,
        showIdleAnimation,
        showWinAnimation,
        playNamedAnimation,
        queueNamedAnimation,
        hideAnimation,
        getActiveSpine,
        getContainer,
        playTensionIn,
        playTensionOut,
        hideTension,
        isTensionVisible,
      ],
    );

    return (
      <Container
        ref={outerContainerRef}
        y={y}
        x={x}
        zIndex={1}
        sortableChildren
        name={`pc_overlay_outer_${reelIndex}_${index}`}
      >
        {/* Main overlay container — coin/jackpot/win spines */}
        <Container
          ref={containerRef}
          zIndex={7}
          sortableChildren
          name={containerName}
        />
        {/* Tension container — separate layer on top, independent spine lifecycle */}
        <Container
          ref={tensionContainerRef}
          zIndex={10}
          sortableChildren
          name={`pc_tension_${reelIndex}_${index}`}
        />
      </Container>
    );
  },
);

PotCollectionOverlaySymbol.displayName = "PotCollectionOverlaySymbol";

export default PotCollectionOverlaySymbol;
